import React, { useState } from "react";
import { svgPathMap } from "./svgMapConstants";
import { mapRoutes } from "../../constants/covering";
import SvgMapModal from "./SvgMapModal";
import "./styles/svgMap.css";

const jsxOptions = { throwIfNamespace: false };

function ReactSvgMap({
  svgPathData = svgPathMap,
  coveringData = [],
  width = 800.36395,
  height = 600.29416,
  viewBox = "0 0 650.364 550.293",
  pathColor = "#44bb92",
  pathSelectedColor,
  pathDisabledColor = "gray",
  pathStrokeColor = "white",
  pathStrokeWidth = 0.2,
  textColor = "white",
  textSelectedColor = "white",
  textFontSize = 10,
  textFontFamily,
  withHiddenMargins = true,
  containerClassName = "",
  wrapperClassName = "",
  onPathClick,
  modalContentComponent,
}) {
  const [selectedPath, setSelectedPath] = useState(null);

  const handlePathClick = (path) => {
    const isSame = selectedPath?.id === path.id;
    setSelectedPath(isSame ? null : path);
    onPathClick?.(path);
  };

  const handleCloseModal = () => setSelectedPath(null);

  const getIsPathDisabled = (path) => !!path.disabled;

  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    return null;
  }

  const selectedSymbol = selectedPath?.symbol;

  const selectedGroupSymbols =
    coveringData.find((entry) => entry.codes.includes(selectedSymbol))?.codes ||
    [];

  const getPathColor = (path) => {
    if (path.disabled) return pathDisabledColor;
    const isInSelectedGroup =
      selectedGroupSymbols.includes(path.symbol) && selectedPath;
    if (isInSelectedGroup) return selectedPath?.pathColor;
    return path.pathColor ?? pathColor;
  };

  const getTextColor = (path) =>
    selectedPath?.id === path.id
      ? textSelectedColor
      : path.textColor ?? textColor;

  // const arrowSize = 10;

  // const renderArrows = ({ centerDot, arrows, nod }) => {
  //   if (!centerDot || !arrows) return null;
  //   const { x, y } = centerDot;
  //   const directions = {
  //     topLeft: [-arrowSize, -arrowSize],
  //     topRight: [arrowSize, -arrowSize],
  //     bottomLeft: [-arrowSize, arrowSize],
  //     bottomRight: [arrowSize, arrowSize],
  //   };

  //   return Object.entries(directions).map(([dir, [dx, dy]]) => {
  //     if (!arrows[dir]) return null;
  //     return (
  //       <line
  //         key={`${nod}-${dir}`}
  //         x1={x}
  //         y1={y}
  //         x2={x + dx}
  //         y2={y + dy}
  //         className="node-arrow"
  //         markerEnd="url(#arrowhead)"
  //       />
  //     );
  //   });
  // };

  return (
    <div className={`map-svg-container ${containerClassName}`}>
      {modalContentComponent && (
        <SvgMapModal
          selectedPath={selectedPath}
          modalContentComponent={modalContentComponent}
          handleCloseModal={handleCloseModal}
        />
      )}

      <div
        className={`map-svg-wrapper ${wrapperClassName}`}
        style={{ overflow: withHiddenMargins ? "hidden" : "visible" }}
        id="map-svg-wrapper"
      >
        <svg
          id="map-svg"
          className="animate-translate"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={viewBox}
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid meet"
          {...jsxOptions}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="6"
              markerHeight="6"
              refX="3"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L6,3 z" fill="black" />
            </marker>
          </defs>

          {svgPathData.map((item) => {
            const { d, id, symbol, textPath } = item;
            return (
              <g key={id}>
                <path
                  d={d}
                  id={id}
                  fill={getPathColor(item)}
                  onClick={() =>
                    !getIsPathDisabled(item) && handlePathClick(item)
                  }
                  style={{
                    stroke: pathStrokeColor,
                    strokeWidth: pathStrokeWidth,
                    cursor: getIsPathDisabled(item) ? "not-allowed" : "pointer",
                  }}
                  className={
                    selectedGroupSymbols.includes(item.symbol)
                      ? "svg-path-group-highlight"
                      : !!selectedPath
                      ? "svg-path-group-not-highlight"
                      : ""
                  }
                />
                {textPath && symbol && (
                  <text
                    key={symbol}
                    transform={textPath}
                    onClick={() =>
                      !getIsPathDisabled(item) && handlePathClick(item)
                    }
                    style={{
                      fill: getTextColor(item),
                      fontSize: textFontSize,
                      fontFamily: textFontFamily,
                      cursor: getIsPathDisabled(item)
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    {symbol}
                  </text>
                )}
              </g>
            );
          })}

          {mapRoutes.map(({ from, to, via = [] }, index) => {
            if (!from || !to) return null;

            let pathD = `M${from.x},${from.y}`;
            if (via.length === 1) {
              const [q] = via;
              pathD += ` Q${q.x},${q.y} ${to.x},${to.y}`;
            } else if (via.length > 1) {
              for (let i = 0; i < via.length - 1; i++) {
                const ctrl = via[i];
                const next = via[i + 1];
                pathD += ` Q${ctrl.x},${ctrl.y} ${next.x},${next.y}`;
              }
              pathD += ` T${to.x},${to.y}`;
            } else {
              pathD += ` L${to.x},${to.y}`;
            }

            return (
              <path key={`route-${index}`} className="route-line" d={pathD} />
            );
          })}

          {coveringData.map((entry) => {
            const { nod, centerDot } = entry;
            if (!centerDot) return null;
            const { x, y } = centerDot;
            const size = 5;
            const points = [
              `${x},${y - size}`,
              `${x + size},${y}`,
              `${x},${y + size}`,
              `${x - size},${y}`,
            ].join(" ");
            return (
              <g key={`group-${nod}`}>
                <polygon
                  points={points}
                  fill="black"
                  stroke="white"
                  strokeWidth="1"
                />
                {/* {renderArrows(entry)} */}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export default ReactSvgMap;
